<template>
    <div class="events-filters-panel">
        <div class="events-filters-panel-inner">
            <div class="header">
                <button class="back-caret" @click.prevent="emit('close')" aria-label="Close">
                    <SvgsBackCaret />
                </button>
                <h2 class="subhead-2">Filter Events</h2>
                <button class="reset small-heavy uppercase" @click="resetAll">Reset</button>
            </div>

            <!-- Day. Checkboxes, exactly like Time of Day below, and mirroring
                 the inline pills — same state.

                 There is no "Both" option any more. It existed because the group
                 was radios, where the cleared state had nothing checked and read
                 as broken: Reset appeared to do nothing (Asana O2-30). As a list,
                 no boxes ticked and both boxes ticked both mean "every day", so
                 the cleared state is self-evident and Reset visibly clears it. -->
            <div class="checkboxes">
                <fieldset>
                    <legend>Day</legend>
                    <div class="checkbox-wrapper" v-for="day in days" :key="day.value">
                        <label>
                            {{ day.label }}
                            <input type="checkbox" v-model="draft.days" :value="day.value" />
                        </label>
                    </div>
                </fieldset>
            </div>

            <!-- Time of Day -->
            <div class="checkboxes">
                <fieldset>
                    <legend>Time of Day</legend>
                    <div class="checkbox-wrapper" v-for="slot in timeSlots" :key="slot.value">
                        <label>
                            {{ slot.label }}
                            <input type="checkbox" v-model="draft.times" :value="slot.value" />
                        </label>
                    </div>
                </fieldset>
            </div>

            <!-- Neighborhood -->
            <label class="card-header" for="events-hood-select">Neighborhood</label>
            <div class="select form-field sort-by">
                <select id="events-hood-select" v-model="draft.neighborhood">
                    <option :value="null">All Neighborhoods</option>
                    <!-- Grouped by region rather than one flat list of 27 (O2-32).
                         Regions with no events drop out, so the headings never
                         sit above an empty group. -->
                    <optgroup v-for="group in groupedNeighborhoods" :key="group.label" :label="group.label">
                        <option v-for="hood in group.options" :key="hood.id" :value="hood.id">
                            {{ hood.name }}
                        </option>
                    </optgroup>
                </select>
                <SvgsSelectArrow class="select-arrow" />
            </div>

            <div class="apply-button-wrapper">
                <div class="container hd-container">
                    <button
                        class="apply-cancel-buttons apply-button"
                        :class="{ active: dirty }"
                        :disabled="!dirty"
                        @click="applyFilters"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    const props = defineProps({
        // { days, times, neighborhood } — same shape the events page holds.
        modelValue: {
            type: Object,
            required: true,
        },
        days: {
            type: Array,
            required: true,
        },
        neighborhoods: {
            type: Array,
            required: true,
        },
    });

    // Region-grouped for the dropdown's optgroups — see useNeighborhoodGroups.
    const groupedNeighborhoods = computed(() => groupNeighborhoods(props.neighborhoods || []));

    const emit = defineEmits(['update:modelValue', 'close']);

    const timeSlots = [
        { value: 'morning', label: 'Morning' },
        { value: 'afternoon', label: 'Afternoon' },
    ];

    // Edited locally and committed on Apply, matching how the sites and
    // city-tours panels behave — closing without applying changes nothing.
    const draft = ref(cloneFilters(props.modelValue));

    watch(
        () => props.modelValue,
        (next) => (draft.value = cloneFilters(next)),
        { deep: true },
    );

    function cloneFilters(value) {
        return { days: [...(value.days || [])], times: [...(value.times || [])], neighborhood: value.neighborhood ?? null };
    }

    const dirty = computed(() => {
        const a = draft.value;
        const b = props.modelValue;
        return (
            a.days.length !== (b.days || []).length ||
            a.days.some((d) => !(b.days || []).includes(d)) ||
            a.neighborhood !== (b.neighborhood ?? null) ||
            a.times.length !== (b.times || []).length ||
            a.times.some((t) => !(b.times || []).includes(t))
        );
    });

    function resetAll() {
        draft.value = { days: [], times: [], neighborhood: null };
    }

    function applyFilters() {
        emit('update:modelValue', { ...props.modelValue, ...draft.value });
        emit('close');
    }
</script>

<style lang="scss" scoped>
    // The flyout's .container carries no padding of its own (it's commented out
    // in container.scss), so each panel pads itself. These are the same values
    // Filters/sites.vue uses, and the bottom allowance clears the fixed Apply bar.
    .events-filters-panel {
        height: 100%;
        overflow: hidden;
        overflow-y: scroll;
        padding: 16px;
        padding-bottom: calc(98px + env(safe-area-inset-bottom));

        @media (min-width: $tablet) {
            padding: 64px 64px calc(98px + env(safe-area-inset-bottom)) 64px;
        }
    }

    // Centred title with the caret and reset pinned to the edges, as the other
    // filter panels do.
    .header {
        position: relative;
        text-align: center;
        margin-bottom: 34px;

        .back-caret {
            position: absolute;
            left: 0;
            top: -3px;
        }

        // Quieter than the label they sit beside: these explain the control, they
        // aren't part of it.
        .legend-note,
        .option-note {
            font-weight: 400;
            text-transform: none;
            opacity: 0.6;
        }

        .reset {
            position: absolute;
            right: 0;
            top: 2px;
        }
    }

    .card-header {
        display: block;
        margin-top: 24px;
        margin-bottom: 8px;

        font-family: $body;
        font-size: 12px;
        font-weight: $heavy;
        letter-spacing: 0.72px;
        text-transform: uppercase;
        color: $gray-darkest;
    }

    .checkboxes {
        margin-top: 24px;

        legend {
            margin-bottom: 8px;

            font-family: $body;
            font-size: 12px;
            font-weight: $heavy;
            letter-spacing: 0.72px;
            text-transform: uppercase;
            color: $gray-darkest;
        }

        // forms-filters.scss carries `.checkboxes .checkbox-wrapper span
        // { margin-left: 12px }`, written for a span that trails something else
        // in the row. "Both" is the only option whose text is wrapped in a span
        // — the wrapper keeps the label to two flex children — so it was the
        // only row indented 12px past the rest. The nested .option-note is
        // still a span and keeps the margin, which is what holds "(default)"
        // off the word.
        .checkbox-wrapper label > span {
            margin-left: 0;
        }

        .checkbox-wrapper label {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;

            padding: 12px 0;
            border-bottom: 1px solid $gray-light-2;

            font-family: $body;
            font-size: 16px;
            font-weight: $book;
            color: $black;
            cursor: pointer;
        }

        input {
            width: 18px;
            height: 18px;
            accent-color: $turquoise;
        }
    }

    // Pinned to the bottom of the viewport like the other panels', so Apply is
    // always reachable rather than sitting below a long list of options.
    .apply-button-wrapper {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        // Clear of the iPhone home indicator (viewport-fit=cover is on).
        padding: 10px 0 calc(10px + env(safe-area-inset-bottom));
        background: $white;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
        z-index: 9999;

        @media (min-width: $tablet) {
            padding: 20px 0 calc(20px + env(safe-area-inset-bottom));
        }
    }
</style>
